/**
 * ETL block palette (ADR-0038, P4) — the left rail of draggable blocks, grouped
 * by category, search-filtered. Driven entirely by the backend block catalog
 * (`etlApi.blocks()`), so a new backend block appears here with zero FE change.
 *
 * Drag carries the block type + descriptor JSON on the dataTransfer; the editor's
 * `onDrop` reads them to spawn a node at the drop point.
 */
import * as React from "react";
import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import type { BlockCatalog, BlockDescriptor } from "@/lib/api-etl";
import { cn } from "@/lib/cn";

const CATEGORY_LABEL: Record<string, string> = {
  source: "Sorgenti",
  transform: "Trasformazioni",
  enricher: "Arricchimenti",
  sink: "Destinazioni",
};
const CATEGORY_ORDER = ["source", "transform", "enricher", "sink"];

function onBlockDragStart(e: React.DragEvent, descriptor: BlockDescriptor): void {
  e.dataTransfer.setData("application/etl-block-type", descriptor.type);
  e.dataTransfer.setData("application/etl-descriptor", JSON.stringify(descriptor));
  e.dataTransfer.effectAllowed = "move";
}

export function BlockPalette({
  catalog,
  onPick,
  disabled,
}: {
  catalog: BlockCatalog;
  /** Click-to-add fallback (drop is primary); the editor places it at center. */
  onPick?: (descriptor: BlockDescriptor) => void;
  disabled?: boolean;
}) {
  const [q, setQ] = React.useState("");
  const query = q.trim().toLowerCase();

  const groups = CATEGORY_ORDER.map((cat) => {
    const blocks = (catalog[cat] ?? []).filter(
      (b) =>
        !query ||
        b.ui.label.toLowerCase().includes(query) ||
        b.description.toLowerCase().includes(query) ||
        b.type.toLowerCase().includes(query),
    );
    return { cat, blocks };
  }).filter((g) => g.blocks.length > 0);

  return (
    <div className="flex h-full w-60 shrink-0 flex-col border-r border-border/60 bg-surface">
      <div className="border-b border-border/60 p-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca blocchi…"
            className="h-8 pl-7 text-sm"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {groups.length === 0 && (
          <p className="px-1 py-4 text-center text-xs text-muted-foreground">Nessun blocco</p>
        )}
        {groups.map((g) => (
          <div key={g.cat} className="mb-3">
            <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {CATEGORY_LABEL[g.cat] ?? g.cat}
            </div>
            <div className="space-y-1">
              {g.blocks.map((b) => (
                <button
                  key={b.type}
                  type="button"
                  draggable={!disabled}
                  onDragStart={(e) => !disabled && onBlockDragStart(e, b)}
                  onClick={() => !disabled && onPick?.(b)}
                  disabled={disabled}
                  className={cn(
                    "flex w-full items-start gap-2 rounded-md border border-transparent px-2 py-1.5 text-left",
                    "hover:border-border hover:bg-elevated disabled:opacity-50",
                    disabled ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing",
                  )}
                  title={b.description}
                >
                  <span
                    className="mt-1 h-2 w-2 shrink-0 rounded-full"
                    style={{ background: b.ui.color }}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-medium text-foreground">
                      {b.ui.label}
                    </span>
                    <span className="block truncate text-[10px] text-muted-foreground">
                      {b.description}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
